/**
 * The flagship guides — the editorial layer on top of the library.
 *
 * The blog publishes several posts a day and holds 421 of them. Presenting all
 * of that as one undifferentiated feed is what made the homepage feel like a
 * listing UI: the deepest guide on the site sat in the same card as the
 * fourth "Best X in 2026" of the week.
 *
 * So the homepage is a magazine on top of a library. A small curated set gets
 * the hero and the prominent rows; everything else lives in the browsable feed
 * below. Readers forgive a library for being large. They do not forgive a
 * magazine for repeating itself.
 *
 * ── Maintaining this list ──
 * FLAGSHIP_SLUGS is the editorial decision and the only thing to edit. Add the
 * guide you want people to land on; remove one that has gone stale. Order is
 * the order they appear. A slug that no longer exists is ignored rather than
 * throwing, so deleting a post cannot break the homepage — a test flags it.
 *
 * When the list is empty the homepage falls back to the previous automatic
 * behavior, so this is additive: nothing breaks if nobody curates.
 */

/**
 * Hand-picked. The seed set is the substantial, evergreen guides that carry an
 * interactive diagram — the pieces that show what this blog is for, rather
 * than the most recent thing published.
 */
export const FLAGSHIP_SLUGS: readonly string[] = [
  "ai-seo-geo-complete-guide",
  "how-ai-search-engines-decide-what-to-cite",
  "entity-seo-knowledge-graph-authority",
  "link-building-guide",
  "content-seo-guide",
  "ultimate-guide-to-keyword-research",
];

export interface FlagshipCandidate {
  slug: string;
  readingTime: number;
  categories: string[];
  publishedAt: string;
}

/**
 * Resolve the curated list against the posts that actually exist, in curated
 * order. Returns [] when nothing is curated, which callers treat as "fall back
 * to the automatic selection".
 */
export function flagshipPosts<T extends FlagshipCandidate>(posts: readonly T[]): T[] {
  const bySlug = new Map(posts.map((p) => [p.slug, p]));
  return FLAGSHIP_SLUGS.map((slug) => bySlug.get(slug)).filter((p): p is T => Boolean(p));
}

/**
 * Slugs in the curated list that no longer match a published post. Surfaced by
 * a test so a renamed or unpublished flagship is caught at build time rather
 * than quietly shrinking the homepage.
 */
export function missingFlagships(posts: readonly FlagshipCandidate[]): string[] {
  const slugs = new Set(posts.map((p) => p.slug));
  return FLAGSHIP_SLUGS.filter((slug) => !slugs.has(slug));
}
